import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const m = (v) => `$${Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pctCls = (v) => (v == null ? 'text-slate-400' : v < 15 ? 'text-rose-600' : v < 30 ? 'text-amber-600' : 'text-emerald-600');
const PRESETS = [7, 30, 90];

const SORTS = {
  real: [['profit', 'Ganancia $'], ['pct_sale', '% s/venta'], ['qty', 'Piezas'], ['revenue', 'Venta $']],
  catalog: [['profit', 'Ganancia por pieza'], ['pct_sale', '% s/venta'], ['pct_cost', '% s/costo']],
};

const Btn = ({ on, children, ...p }) => (
  <button {...p} className={`px-3 py-1 rounded-md text-xs font-semibold ${on ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-sky-50'}`}>{children}</button>
);

const Name = ({ r }) => (
  <td className="px-3 py-2">
    <div className="font-medium">{r.name}{r.variant_name && <span className="text-primary"> · {r.variant_name}</span>}</div>
    <div className="text-[11px] text-slate-500">SKU: {r.sku || '—'}{!r.has_cost && <span className="ml-2 text-orange-600 font-semibold">Sin costo</span>}</div>
  </td>
);

const RealTable = ({ rows }) => (
  <table className="w-full text-sm border rounded" data-testid="ranking-real-table">
    <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr>
      <th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Producto</th><th className="px-3 py-2 text-right">Piezas</th>
      <th className="px-3 py-2 text-right">Venta</th><th className="px-3 py-2 text-right">Comisiones</th><th className="px-3 py-2 text-right">Costo</th>
      <th className="px-3 py-2 text-right">Ganancia</th><th className="px-3 py-2 text-right">% s/venta</th></tr></thead>
    <tbody className="divide-y">{rows.map((r, i) => (
      <tr key={r.id} data-testid={`ranking-real-row-${r.id}`}>
        <td className="px-3 py-2 font-bold text-slate-400">{i + 1}</td><Name r={r} />
        <td className="px-3 py-2 text-right">{r.qty}</td><td className="px-3 py-2 text-right">{m(r.revenue)}</td>
        <td className="px-3 py-2 text-right">{m(r.commission)}</td><td className="px-3 py-2 text-right">{m(r.cost_total)}</td>
        <td className={`px-3 py-2 text-right font-bold ${r.profit < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{m(r.profit)}</td>
        <td className={`px-3 py-2 text-right font-semibold ${pctCls(r.pct_sale)}`}>{r.pct_sale == null ? '—' : `${r.pct_sale}%`}</td>
      </tr>))}</tbody>
  </table>
);

const CatalogTable = ({ rows }) => (
  <table className="w-full text-sm border rounded" data-testid="ranking-catalog-table">
    <thead className="bg-slate-50 text-[11px] uppercase text-slate-500"><tr>
      <th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Producto</th><th className="px-3 py-2 text-right">Precio 1</th>
      <th className="px-3 py-2 text-right">Comisión</th><th className="px-3 py-2 text-right">Costo</th><th className="px-3 py-2 text-right">Ganancia/pza</th>
      <th className="px-3 py-2 text-right">% s/venta</th><th className="px-3 py-2 text-right">% s/costo</th><th className="px-3 py-2 text-right">Stock</th></tr></thead>
    <tbody className="divide-y">{rows.map((r, i) => (
      <tr key={r.id} data-testid={`ranking-catalog-row-${r.id}`}>
        <td className="px-3 py-2 font-bold text-slate-400">{i + 1}</td><Name r={r} />
        <td className="px-3 py-2 text-right">{m(r.sale)}</td><td className="px-3 py-2 text-right">{m(r.commission)}</td><td className="px-3 py-2 text-right">{m(r.cost)}</td>
        <td className={`px-3 py-2 text-right font-bold ${r.profit < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{m(r.profit)}</td>
        <td className={`px-3 py-2 text-right font-semibold ${pctCls(r.pct_sale)}`}>{r.pct_sale}%</td>
        <td className="px-3 py-2 text-right">{r.pct_cost == null ? '—' : `${r.pct_cost}%`}</td><td className="px-3 py-2 text-right">{r.stock}</td>
      </tr>))}</tbody>
  </table>
);

export const ProfitRankingDialog = ({ open, onClose }) => {
  const [view, setView] = useState('real');
  const [days, setDays] = useState(30);
  const [custom, setCustom] = useState('');
  const [sort, setSort] = useState('profit');
  const [asc, setAsc] = useState(false);
  const [onlyCost, setOnlyCost] = useState(false);
  const [d, setD] = useState(null);

  useEffect(() => {
    if (!open) return;
    setD(null);
    axios.get(`${API}/products/profit-ranking?days=${days}`, { withCredentials: true })
      .then(r => setD(r.data)).catch(e => toast.error(e.response?.data?.detail || 'Error al cargar'));
  }, [open, days]);

  const rows = useMemo(() => {
    if (!d) return [];
    const list = d[view].filter(r => !onlyCost || r.has_cost);
    return [...list].sort((a, b) => ((a[sort] ?? -Infinity) - (b[sort] ?? -Infinity)) * (asc ? 1 : -1));
  }, [d, view, sort, asc, onlyCost]);

  const switchView = (v) => { setView(v); setSort('profit'); };
  const t = d?.totals;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="ranking-dialog">
        <DialogHeader><DialogTitle>🏆 Ranking de ganancia {d?.branch_name ? `· ${d.branch_name}` : ''}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Btn on={view === 'real'} onClick={() => switchView('real')} data-testid="ranking-view-real">Ganancia real de lo vendido</Btn>
          <Btn on={view === 'catalog'} onClick={() => switchView('catalog')} data-testid="ranking-view-catalog">Ganancia por pieza (catálogo)</Btn>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {view === 'real' && <>
            <span className="text-slate-600">Periodo:</span>
            {PRESETS.map(p => <Btn key={p} on={days === p} onClick={() => setDays(p)} data-testid={`ranking-days-${p}`}>{p} días</Btn>)}
            <Input type="number" min="1" value={custom} onChange={e => setCustom(e.target.value)} placeholder="Otro" className="w-20 h-8" data-testid="ranking-days-custom" />
            <Btn on={!PRESETS.includes(days)} onClick={() => parseInt(custom, 10) > 0 ? setDays(parseInt(custom, 10)) : toast.error('Días inválidos')} data-testid="ranking-days-apply">Ver</Btn>
            <span className="mx-2 text-slate-300">|</span>
          </>}
          <span className="text-slate-600">Ordenar:</span>
          {SORTS[view].map(([k, l]) => <Btn key={k} on={sort === k} onClick={() => setSort(k)} data-testid={`ranking-sort-${k}`}>{l}</Btn>)}
          <Btn on={asc} onClick={() => setAsc(!asc)} data-testid="ranking-sort-dir">{asc ? '↑ Menor primero' : '↓ Mayor primero'}</Btn>
          <label className="flex items-center gap-1 text-xs ml-2"><input type="checkbox" checked={onlyCost} onChange={e => setOnlyCost(e.target.checked)} data-testid="ranking-only-cost" /> Solo con costo</label>
        </div>
        {!d ? <p className="text-sm text-muted-foreground py-8 text-center">Cargando…</p> : (
          <div className="space-y-3">
            {view === 'real' && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm" data-testid="ranking-totals">
                {[['Venta', t.revenue], ['Comisiones', t.commission], ['Costo', t.cost], ['Ganancia', t.profit]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border p-3"><div className="text-[11px] uppercase text-slate-500">{l}</div><div className={`text-lg font-bold ${l === 'Ganancia' ? 'text-emerald-700' : ''}`}>{m(v)}</div></div>
                ))}
                <div className="rounded-lg border p-3"><div className="text-[11px] uppercase text-slate-500">Productos sin costo</div><div className={`text-lg font-bold ${t.missing_cost ? 'text-orange-600' : ''}`}>{t.missing_cost}</div></div>
              </div>
            )}
            {rows.length === 0 ? <p className="text-sm text-center text-slate-500 py-6">Sin datos</p> : view === 'real' ? <RealTable rows={rows} /> : <CatalogTable rows={rows} />}
            <p className="text-[11px] text-slate-500">Ganancia = venta − comisión − costo. Lo vendido incluye tienda y envíos de esta sucursal. Si un producto no tiene costo, su ganancia sale más alta de lo real.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
